<template>
  <div class="survey-setup-container">
    <div class="survey-header">
      <h2>
        <i class="fas fa-poll"></i>
        Survey Campaigns
      </h2>
      <div class="header-buttons">
        <button
          v-if="selectedCampaign"
          @click="refreshCampaignData"
          class="refresh-btn"
          title="Refresh campaign data"
        >
          <i class="fas fa-sync-alt"></i>
        </button>
        <button @click="$emit('close')" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <div class="survey-body">
      <!-- Campaign Selection and Delete Button -->
      <div class="section campaign-selector-row">
        <div class="select-group">
          <label for="campaignSelect">Select Campaign:</label>
          <div class="select-wrapper">
            <select
              id="campaignSelect"
              v-model="selectedCampaignId"
              @change="onCampaignChange"
            >
              <option :value="null">-- Select Campaign --</option>
              <option
                v-for="campaign in campaigns"
                :key="campaign.id"
                :value="campaign.id"
              >
                {{ campaign.title }} ({{ campaign.response_count }} responses)
              </option>
              <option value="NEW">+ New Campaign...</option>
            </select>
          </div>
        </div>
        <button
          v-if="selectedCampaign && !showCreateDialog"
          @click="confirmDelete"
          class="btn-danger btn-delete-inline"
        >
          <i class="fas fa-trash"></i> Delete Survey
        </button>
      </div>

      <!-- Create Campaign Dialog -->
      <div v-if="showCreateDialog" class="create-dialog">
        <h3>Create New Campaign</h3>
        <p class="dialog-info">
          Enter a title and description for your survey campaign.
        </p>
        <div class="form-group">
          <label for="newTitle">Campaign Title:</label>
          <input
            id="newTitle"
            v-model="newCampaignTitle"
            type="text"
            placeholder="e.g., Resident Satisfaction Survey"
            maxlength="255"
            @keyup.enter="createCampaign"
            ref="titleInput"
          />
          <span v-if="createError" class="error-text">{{ createError }}</span>
        </div>
        <div class="form-group">
          <label for="newDescription">Description:</label>
          <textarea
            id="newDescription"
            v-model="newCampaignDescription"
            placeholder="e.g., Help us improve by answering a few questions"
            maxlength="500"
            rows="3"
          ></textarea>
        </div>
        <div class="form-group">
          <label for="campaignString">Campaign String (for URL):</label>
          <input
            id="campaignString"
            v-model="newCampaignString"
            type="text"
            placeholder="e.g., resident-satisfaction-2025"
            maxlength="255"
          />
          <span class="helper-text">Unique identifier for the campaign</span>
        </div>
        <div class="button-group">
          <button @click="createCampaign" class="btn-primary">Create</button>
          <button @click="cancelCreate" class="btn-secondary">Cancel</button>
        </div>
      </div>

      <!-- Campaign Details -->
      <div
        v-if="selectedCampaign && !showCreateDialog"
        class="campaign-details"
      >
        <!-- QR Code and Campaign Info -->
        <div class="section qr-and-info-section">
          <div class="qr-and-info-row">
            <div class="qr-section">
              <h3>QR Code</h3>
              <transition name="slide-fade">
                <div v-if="!qrDetailsCollapsed" class="qr-container">
                  <div id="qrcode" ref="qrCodeContainer"></div>
                </div>
              </transition>
            </div>

            <div class="campaign-info">
              <h3 @click="toggleQrDetails" class="collapsible-header">
                Campaign Details
                <i
                  class="fas toggle-icon"
                  :class="
                    qrDetailsCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'
                  "
                ></i>
              </h3>
              <transition name="slide-fade">
                <div v-if="!qrDetailsCollapsed">
                  <div class="info-row">
                    <span class="label">Campaign Title:</span>
                    <span
                      v-if="!editingField || editingField !== 'title'"
                      class="value editable"
                      @dblclick="startEditing('title')"
                      :title="'Double-click to edit'"
                    >
                      {{ selectedCampaign.title }}
                    </span>
                    <input
                      v-else
                      v-model="editValue"
                      @blur="saveEdit('title')"
                      @keyup.enter="saveEdit('title')"
                      @keyup.esc="cancelEdit"
                      ref="editInput"
                      class="edit-input"
                      maxlength="255"
                    />
                  </div>
                  <div class="info-row description-row">
                    <span class="label">Description:</span>
                    <span
                      v-if="!editingField || editingField !== 'description'"
                      class="value editable"
                      @dblclick="startEditing('description')"
                      :title="'Double-click to edit'"
                    >
                      {{
                        selectedCampaign.description ||
                        '(No description - click to add)'
                      }}
                    </span>
                    <input
                      v-else
                      v-model="editValue"
                      @blur="saveEdit('description')"
                      @keyup.enter="saveEdit('description')"
                      @keyup.esc="cancelEdit"
                      ref="editTextarea"
                      class="edit-input"
                      maxlength="500"
                    />
                  </div>
                  <div class="info-row">
                    <span class="label">Created By:</span>
                    <span class="value">{{
                      selectedCampaign.creator_name
                    }}</span>
                    <span class="label" style="margin-left: 20px"
                      >Created On:</span
                    >
                    <span class="value">{{
                      formatDate(selectedCampaign.created_on)
                    }}</span>
                  </div>
                  <div class="info-row">
                    <span class="label">Responses:</span>
                    <span class="value">{{ responses.length }}</span>
                  </div>
                </div>
              </transition>
            </div>
          </div>

          <!-- Download Button and URL -->
          <transition name="slide-fade">
            <div v-if="!qrDetailsCollapsed" class="qr-url-section">
              <button @click="downloadQR" class="btn-secondary btn-download">
                <i class="fas fa-download"></i> Download QR Code
              </button>
              <div class="qr-url">
                <strong>URL:&nbsp;</strong>
                <a :href="surveyUrl" target="_blank">{{ surveyUrl }}</a>
              </div>
            </div>
          </transition>
        </div>

        <!-- Question Builder -->
        <div class="section question-builder">
          <div class="question-header-row">
            <div class="question-title-and-buttons">
              <h3>Questions</h3>
              <div class="question-action-buttons">
                <button
                  @click="addQuestion"
                  class="btn-icon-action"
                  title="Add Question"
                >
                  <i class="fas fa-plus"></i>
                </button>
                <button
                  @click="saveQuestions"
                  class="btn-icon-action"
                  :disabled="!hasQuestionChanges"
                  title="Save Questions"
                >
                  <i class="fas fa-save"></i>
                </button>
                <button
                  @click="removeQuestion(currentQuestionIndex)"
                  class="btn-icon-action btn-remove"
                  :disabled="questions.length === 0"
                  title="Delete Current Question"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
            <i
              @click="toggleQuestions"
              class="fas toggle-icon-right"
              :class="questionsCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"
            ></i>
          </div>
          <transition name="slide-fade">
            <div v-if="!questionsCollapsed" class="questions-carousel">
              <div v-if="questions.length === 0" class="no-questions">
                <p>No questions yet. Click the + button to add a question.</p>
              </div>
              <div v-else class="question-item">
                <div class="question-content">
                  <div class="form-row">
                    <div class="form-group">
                      <label>Question Type:</label>
                      <select
                        v-model="questions[currentQuestionIndex].type"
                        class="question-type-select"
                        @blur="markQuestionsChanged"
                      >
                        <option value="freeform">Freeform Text</option>
                        <option value="yesno">Yes/No</option>
                        <option value="multiple_choice">Multiple Choice</option>
                      </select>
                    </div>
                    <div class="form-group sequence-group">
                      <label>Sequence:</label>
                      <input
                        type="number"
                        v-model.number="
                          questions[currentQuestionIndex].sequence
                        "
                        min="1"
                        :max="questions.length"
                        class="sequence-input"
                        @change="resequenceQuestions(currentQuestionIndex)"
                      />
                    </div>
                    <div class="form-group">
                      <label>Question Text:</label>
                      <textarea
                        v-model="questions[currentQuestionIndex].text"
                        rows="2"
                        placeholder="Enter your question..."
                        class="question-text-input"
                        @blur="markQuestionsChanged"
                      ></textarea>
                    </div>
                    <div
                      v-if="
                        questions[currentQuestionIndex].type ===
                        'multiple_choice'
                      "
                      class="form-group"
                    >
                      <label>Options: prefix with A), B), C), D):</label>
                      <textarea
                        v-model="questions[currentQuestionIndex].optionsText"
                        rows="4"
                        placeholder="A) Option 1&#10;B) Option 2&#10;C) Option 3&#10;D) Option 4"
                        class="options-input"
                        @blur="markQuestionsChanged"
                      ></textarea>
                    </div>
                  </div>
                </div>
                <div class="question-navigation">
                  <button
                    @click="prevQuestion"
                    :disabled="currentQuestionIndex === 0"
                    class="btn-nav"
                  >
                    <i class="fas fa-chevron-left"></i> Previous
                  </button>
                  <div class="question-info">
                    <span class="question-number"
                      >Question {{ currentQuestionIndex + 1 }} of
                      {{ questions.length }}</span
                    >
                    <div class="question-dots">
                      <span
                        v-for="(q, index) in questions"
                        :key="index"
                        @click="goToQuestion(index)"
                        class="dot"
                        :class="{ active: index === currentQuestionIndex }"
                      ></span>
                    </div>
                  </div>
                  <button
                    @click="nextQuestion"
                    :disabled="currentQuestionIndex === questions.length - 1"
                    class="btn-nav"
                  >
                    Next <i class="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </transition>
        </div>

        <!-- Responses Table -->
        <div class="section responses-section">
          <h3>Responses ({{ responses.length }})</h3>
          <div class="table-container">
            <table class="responses-table">
              <thead>
                <tr>
                  <th>Submitted</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="response in responses"
                  :key="response.id"
                  @click="viewResponse(response.id)"
                  class="clickable-row"
                >
                  <td>{{ formatDateTime(response.submitted_on) }}</td>
                  <td>{{ response.first_name }} {{ response.last_name }}</td>
                  <td>{{ formatPhone(response.phone) }}</td>
                  <td>{{ response.email || '—' }}</td>
                </tr>
              </tbody>
            </table>
            <div v-if="responses.length === 0" class="no-data">
              No responses yet
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Response Detail Popup -->
    <div v-if="showResponsePopup" class="popup-overlay" @click="closePopup">
      <div class="popup-container" @click.stop>
        <div class="popup-header">
          <h3>
            Response to: {{ selectedCampaign ? selectedCampaign.title : '' }}
          </h3>
          <div class="popup-actions">
            <button @click="printResponse" class="btn-icon" title="Print">
              <i class="fas fa-print"></i>
            </button>
            <button @click="closePopup" class="btn-icon">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div class="popup-body" ref="printArea">
          <h2 class="print-only-title">
            Response to: {{ selectedCampaign ? selectedCampaign.title : '' }}
          </h2>
          <div v-if="selectedResponse" class="response-detail">
            <div class="respondent-info">
              <div class="info-row-compact">
                <div class="field">
                  <strong>Name:</strong>
                  {{ selectedResponse.firstName }}
                  {{ selectedResponse.lastName }}
                </div>
                <div class="field">
                  <strong>Phone:</strong>
                  {{ formatPhone(selectedResponse.phone) }}
                </div>
                <div class="field email-field">
                  <strong>Email:</strong> {{ selectedResponse.email || '—' }}
                </div>
                <div class="field">
                  <strong>On:</strong>
                  {{ formatDateTime(selectedResponse.submittedOn) }}
                </div>
              </div>
            </div>
            <hr />
            <div class="answers-list">
              <div
                v-for="(answer, index) in selectedResponse.answers"
                :key="answer.id"
                class="answer-item"
              >
                <div class="question-text">
                  <strong>{{ index + 1 }}. {{ answer.questionText }}</strong>
                </div>
                <div class="answer-text">{{ answer.answerText }}</div>
                <hr
                  v-if="index < selectedResponse.answers.length - 1"
                  class="question-separator"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axiosConfig';
import QRCode from 'qrcode';

export default {
  name: 'SurveySetup',
  data() {
    return {
      campaigns: [],
      selectedCampaignId: null,
      selectedCampaign: null,
      questions: [],
      responses: [],
      showCreateDialog: false,
      newCampaignTitle: '',
      newCampaignDescription: '',
      newCampaignString: '',
      createError: '',
      editingField: null,
      editValue: '',
      hasQuestionChanges: false,
      showResponsePopup: false,
      selectedResponse: null,
      qrDetailsCollapsed: false,
      questionsCollapsed: false,
      currentQuestionIndex: 0,
    };
  },
  computed: {
    surveyUrl() {
      if (!this.selectedCampaign) return '';
      return `https://pixie.bigtapapp.com/survey?id=${this.selectedCampaign.hash_id}`;
    },
  },
  async mounted() {
    await this.fetchCampaigns();

    // Load QR details collapse state from localStorage
    const qrCollapsed = localStorage.getItem('surveyQrDetailsCollapsed');
    if (qrCollapsed !== null) {
      this.qrDetailsCollapsed = qrCollapsed === 'true';
    }

    // Load last selected campaign from localStorage
    const lastSelectedId = localStorage.getItem('surveyLastCampaignId');
    if (lastSelectedId && this.campaigns.length > 0) {
      const campaign = this.campaigns.find(
        (c) => c.id === parseInt(lastSelectedId),
      );
      if (campaign) {
        this.selectedCampaignId = campaign.id;
        await this.onCampaignChange();
      }
    }

    this.setupSocketListeners();
  },
  beforeUnmount() {
    this.removeSocketListeners();
  },
  methods: {
    toggleQrDetails() {
      this.qrDetailsCollapsed = !this.qrDetailsCollapsed;
      localStorage.setItem('surveyQrDetailsCollapsed', this.qrDetailsCollapsed);

      // Regenerate QR code when expanding
      if (!this.qrDetailsCollapsed && this.selectedCampaign) {
        this.$nextTick(() => {
          this.generateQRCode();
        });
      }
    },

    toggleQuestions() {
      this.questionsCollapsed = !this.questionsCollapsed;
    },

    ensureQrCollapsed() {
      // Auto-collapse QR section when interacting with questions
      if (!this.qrDetailsCollapsed) {
        this.qrDetailsCollapsed = true;
        localStorage.setItem('surveyQrDetailsCollapsed', 'true');
      }
      // Auto-expand questions section if collapsed
      if (this.questionsCollapsed) {
        this.questionsCollapsed = false;
      }
    },

    async fetchCampaigns() {
      try {
        const response = await axios.get('/api/survey/campaigns');
        this.campaigns = response.data.campaigns || [];
      } catch (error) {
        console.error('Error fetching campaigns:', error);
      }
    },

    async onCampaignChange() {
      if (this.selectedCampaignId === 'NEW') {
        this.showCreateDialog = true;
        this.newCampaignTitle = '';
        this.newCampaignDescription = '';
        this.newCampaignString = '';
        this.createError = '';
        this.$nextTick(() => {
          if (this.$refs.titleInput) {
            this.$refs.titleInput.focus();
          }
        });
        return;
      }

      if (!this.selectedCampaignId) {
        this.selectedCampaign = null;
        this.questions = [];
        this.responses = [];
        return;
      }

      // Save selected campaign to localStorage
      localStorage.setItem(
        'surveyLastCampaignId',
        this.selectedCampaignId.toString(),
      );

      this.selectedCampaign = this.campaigns.find(
        (c) => c.id === this.selectedCampaignId,
      );

      if (this.selectedCampaign) {
        await this.fetchQuestions();
        await this.fetchResponses();
        this.$nextTick(() => {
          this.generateQRCode();
        });
      }
    },

    async fetchQuestions() {
      try {
        const response = await axios.get(
          `/api/survey/questions/${this.selectedCampaignId}`,
        );
        this.questions = (response.data.questions || []).map((q, index) => ({
          id: q.id,
          text: q.text,
          type: q.type,
          options: q.options || [],
          optionsText:
            q.type === 'multiple_choice' && q.options
              ? q.options.join('\n')
              : '',
          sequence: q.sequence || index + 1,
        }));
        // Sort questions by sequence
        this.questions.sort((a, b) => a.sequence - b.sequence);
        this.hasQuestionChanges = false;
      } catch (error) {
        console.error('Error fetching questions:', error);
      }
    },

    async fetchResponses() {
      try {
        const response = await axios.get(
          `/api/survey/responses/${this.selectedCampaignId}`,
        );
        this.responses = response.data.responses || [];
      } catch (error) {
        console.error('Error fetching responses:', error);
      }
    },

    async createCampaign() {
      this.createError = '';

      if (!this.newCampaignTitle.trim()) {
        this.createError = 'Campaign title is required';
        return;
      }

      if (!this.newCampaignString.trim()) {
        this.createError = 'Campaign string is required';
        return;
      }

      try {
        const response = await axios.post('/api/survey/campaigns', {
          title: this.newCampaignTitle,
          description: this.newCampaignDescription,
          campaign_string: this.newCampaignString,
        });

        if (response.data && response.data.success) {
          await this.fetchCampaigns();
          this.selectedCampaignId = response.data.campaignId;
          this.showCreateDialog = false;
          await this.onCampaignChange();
        }
      } catch (error) {
        console.error('Error creating campaign:', error);
        if (
          error.response &&
          error.response.data &&
          error.response.data.error
        ) {
          this.createError = error.response.data.error;
        } else {
          this.createError = 'Error creating campaign';
        }
      }
    },

    cancelCreate() {
      this.showCreateDialog = false;
      this.selectedCampaignId = null;
    },

    async confirmDelete() {
      if (
        confirm(
          `Are you sure you want to delete the campaign "${this.selectedCampaign.title}"?`,
        )
      ) {
        try {
          await axios.delete(
            `/api/survey/campaigns/${this.selectedCampaignId}`,
          );
          await this.fetchCampaigns();
          this.selectedCampaignId = null;
          this.selectedCampaign = null;
          this.questions = [];
          this.responses = [];
        } catch (error) {
          console.error('Error deleting campaign:', error);
          alert('Error deleting campaign');
        }
      }
    },

    startEditing(field) {
      this.editingField = field;
      this.editValue = this.selectedCampaign[field] || '';
      this.$nextTick(() => {
        if (field === 'description' && this.$refs.editTextarea) {
          this.$refs.editTextarea.focus();
        } else if (this.$refs.editInput) {
          this.$refs.editInput.focus();
        }
      });
    },

    async saveEdit(field) {
      if (this.editValue === this.selectedCampaign[field]) {
        this.editingField = null;
        return;
      }

      try {
        await axios.patch(`/api/survey/campaigns/${this.selectedCampaignId}`, {
          [field]: this.editValue,
        });

        this.selectedCampaign[field] = this.editValue;
        this.editingField = null;

        // Update in campaigns list
        const campaign = this.campaigns.find(
          (c) => c.id === this.selectedCampaignId,
        );
        if (campaign) {
          campaign[field] = this.editValue;
        }
      } catch (error) {
        console.error('Error updating campaign:', error);
        alert('Error updating campaign');
        this.editingField = null;
      }
    },

    cancelEdit() {
      this.editingField = null;
      this.editValue = '';
    },

    generateQRCode() {
      const container = this.$refs.qrCodeContainer;
      if (!container) return;

      container.innerHTML = '';

      QRCode.toCanvas(
        this.surveyUrl,
        {
          width: 150,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF',
          },
        },
        (error, canvas) => {
          if (error) {
            console.error('Error generating QR code:', error);
            return;
          }
          container.appendChild(canvas);
        },
      );
    },

    downloadQR() {
      const container = this.$refs.qrCodeContainer;
      const canvas = container?.querySelector('canvas');
      if (!canvas) return;

      const link = document.createElement('a');
      link.download = `survey-${this.selectedCampaign.campaign_string}.png`;
      link.href = canvas.toDataURL();
      link.click();
    },

    addQuestion() {
      this.ensureQrCollapsed();
      this.questions.push({
        text: '',
        type: 'freeform',
        options: [],
        optionsText: '',
        sequence: this.questions.length + 1,
      });
      this.hasQuestionChanges = true;
      // Navigate to the newly added question
      this.currentQuestionIndex = this.questions.length - 1;
    },

    markQuestionsChanged() {
      this.ensureQrCollapsed();
      this.hasQuestionChanges = true;
    },

    resequenceQuestions(currentIndex) {
      this.ensureQrCollapsed();
      const currentQuestion = this.questions[currentIndex];
      const newSequence = currentQuestion.sequence;

      // Validate the new sequence number
      if (newSequence < 1 || newSequence > this.questions.length) {
        currentQuestion.sequence = currentIndex + 1;
        return;
      }

      // Remove the current question from the array
      const [movedQuestion] = this.questions.splice(currentIndex, 1);

      // Insert it at the new position (sequence - 1 because array is 0-indexed)
      this.questions.splice(newSequence - 1, 0, movedQuestion);

      // Renumber all questions sequentially
      this.questions.forEach((q, index) => {
        q.sequence = index + 1;
      });

      // Update the current index to follow the moved question
      this.currentQuestionIndex = newSequence - 1;
      this.hasQuestionChanges = true;
    },

    removeQuestion(index) {
      this.questions.splice(index, 1);
      // Renumber remaining questions
      this.questions.forEach((q, idx) => {
        q.sequence = idx + 1;
      });
      this.hasQuestionChanges = true;
      // Adjust current index if needed
      if (
        this.currentQuestionIndex >= this.questions.length &&
        this.questions.length > 0
      ) {
        this.currentQuestionIndex = this.questions.length - 1;
      } else if (this.questions.length === 0) {
        this.currentQuestionIndex = 0;
      }
    },

    prevQuestion() {
      this.ensureQrCollapsed();
      if (this.currentQuestionIndex > 0) {
        this.currentQuestionIndex--;
      }
    },

    nextQuestion() {
      this.ensureQrCollapsed();
      if (this.currentQuestionIndex < this.questions.length - 1) {
        this.currentQuestionIndex++;
      }
    },

    goToQuestion(index) {
      this.ensureQrCollapsed();
      this.currentQuestionIndex = index;
    },

    async saveQuestions() {
      try {
        // Parse options from text for multiple choice questions
        const questionsToSave = this.questions.map((q) => {
          const question = {
            text: q.text,
            type: q.type,
          };

          if (q.type === 'multiple_choice' && q.optionsText) {
            question.options = q.optionsText
              .split('\n')
              .map((line) => line.trim())
              .filter((line) => line.length > 0);
          }

          return question;
        });

        await axios.post('/api/survey/questions', {
          campaignId: this.selectedCampaignId,
          questions: questionsToSave,
        });

        this.hasQuestionChanges = false;
        await this.fetchQuestions();
      } catch (error) {
        console.error('Error saving questions:', error);
        if (
          error.response &&
          error.response.data &&
          error.response.data.error
        ) {
          alert(error.response.data.error);
        } else {
          alert('Error saving questions');
        }
      }
    },

    async viewResponse(responseId) {
      try {
        const response = await axios.get(
          `/api/survey/response-detail/${responseId}`,
        );
        this.selectedResponse = response.data.response;
        this.showResponsePopup = true;
      } catch (error) {
        console.error('Error fetching response detail:', error);
        alert('Error loading response detail');
      }
    },

    closePopup() {
      this.showResponsePopup = false;
      this.selectedResponse = null;
    },

    printResponse() {
      const printArea = this.$refs.printArea;
      if (!printArea) return;

      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Survey Response</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              .print-only-title { margin-bottom: 20px; text-align: center; }
              .respondent-info { margin-bottom: 20px; }
              .info-row-compact { display: table; width: 100%; margin-bottom: 15px; font-size: 0.85em; }
              .info-row-compact .field { display: table-cell; padding-right: 20px; white-space: nowrap; }
              .info-row-compact .field.email-field { width: 200px; overflow: hidden; text-overflow: ellipsis; }
              .info-row-compact .field strong { margin-right: 5px; }
              .info-row { margin-bottom: 10px; }
              hr { margin: 20px 0; border: none; border-top: 1px solid #ddd; }
              hr.question-separator { margin: 15px 0 0 0; border-top: 1px solid #e8e8e8; }
              .answer-item { margin-bottom: 20px; }
              .question-text { font-weight: bold; margin-bottom: 5px; }
              .answer-text { margin-left: 20px; white-space: pre-wrap; background-color: #f8f9fa; padding: 10px; border-radius: 4px; }
            </style>
          </head>
          <body>
            ${printArea.innerHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    },

    async refreshCampaignData() {
      if (this.selectedCampaignId) {
        await this.fetchQuestions();
        await this.fetchResponses();
      }
    },

    setupSocketListeners() {
      const socket = this.$root.socket;
      if (socket) {
        socket.on('survey_campaign_created', this.handleCampaignCreated);
        socket.on('survey_campaign_updated', this.handleCampaignUpdated);
        socket.on('survey_campaign_deleted', this.handleCampaignDeleted);
        socket.on('survey_questions_updated', this.handleQuestionsUpdated);
        socket.on('survey_response', this.handleNewResponse);
      }
    },

    removeSocketListeners() {
      const socket = this.$root.socket;
      if (socket) {
        socket.off('survey_campaign_created', this.handleCampaignCreated);
        socket.off('survey_campaign_updated', this.handleCampaignUpdated);
        socket.off('survey_campaign_deleted', this.handleCampaignDeleted);
        socket.off('survey_questions_updated', this.handleQuestionsUpdated);
        socket.off('survey_response', this.handleNewResponse);
      }
    },

    handleCampaignCreated() {
      this.fetchCampaigns();
    },

    handleCampaignUpdated(data) {
      if (
        this.selectedCampaignId &&
        data.campaignId === this.selectedCampaignId
      ) {
        this.onCampaignChange();
      }
    },

    handleCampaignDeleted(data) {
      if (
        this.selectedCampaignId &&
        data.campaignId === this.selectedCampaignId
      ) {
        this.selectedCampaignId = null;
        this.selectedCampaign = null;
      }
      this.fetchCampaigns();
    },

    handleQuestionsUpdated(data) {
      if (
        this.selectedCampaignId &&
        data.campaignId === this.selectedCampaignId
      ) {
        this.fetchQuestions();
      }
    },

    handleNewResponse(data) {
      if (
        this.selectedCampaignId &&
        data.campaignId === this.selectedCampaignId
      ) {
        this.fetchResponses();
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    },

    formatDateTime(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    formatPhone(phone) {
      if (!phone || phone.length !== 10) return phone;
      return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
    },
  },
};
</script>

<style scoped>
.survey-setup-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: #fff;
  border-radius: 5px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.survey-header {
  background: #6e5875;
  color: white;
  padding: 5px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
}

.survey-header h2 {
  margin: 0;
  font-size: 1.5em;
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-buttons {
  display: flex;
  gap: 10px;
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
}

.refresh-btn,
.close-btn {
  background: transparent;
  border: none;
  color: white;
  font-size: 1.2em;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 4px;
  transition: background-color 0.3s;
}

.refresh-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
  animation: rotate 0.5s ease-in-out;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.survey-body {
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  padding-top: 7px;
}

.section {
  margin-bottom: 5px;
}

.section h3 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 1em;
  border-bottom: 2px solid #a080a8;
  padding-bottom: 5px;
}

label {
  display: block;
  margin-bottom: 6px;
  color: #333;
  font-weight: 600;
  font-size: 0.9em;
}

.campaign-selector-row {
  display: flex;
  align-items: flex-end;
  gap: 10px;
}

.select-group {
  flex: 1;
}

.btn-delete-inline {
  flex-shrink: 0;
  white-space: nowrap;
  margin-bottom: 0;
}

.select-wrapper {
  position: relative;
}

select {
  width: 100%;
  padding: 8px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.9em;
  background: white;
  cursor: pointer;
  transition: border-color 0.3s;
}

select:focus {
  outline: none;
  border-color: #a080a8;
}

.btn-danger {
  background: #a080a8;
  color: white;
}

.btn-danger:hover {
  background: #8a6a8f;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(160, 128, 168, 0.4);
}

.create-dialog {
  background: #f9f9f9;
  padding: 15px;
  border-radius: 6px;
  border: 2px solid #a080a8;
  margin-bottom: 15px;
}

.create-dialog h3 {
  margin: 0 0 8px 0;
  color: #a080a8;
  font-size: 1em;
}

.dialog-info {
  color: #666;
  font-size: 0.85em;
  margin-bottom: 15px;
}

.form-group {
  margin-bottom: 15px;
}

input,
textarea {
  width: 100%;
  padding: 8px;
  border: 2px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.9em;
  transition: border-color 0.3s;
  box-sizing: border-box;
  font-family: inherit;
}

input:focus,
textarea:focus {
  outline: none;
  border-color: #a080a8;
}

textarea {
  resize: vertical;
  min-height: 60px;
}

.helper-text {
  color: #888;
  font-size: 0.8em;
  margin-top: 4px;
  display: block;
}

.error-text {
  color: #f44336;
  font-size: 0.85em;
  margin-top: 4px;
  display: block;
}

.button-group {
  display: flex;
  gap: 8px;
}

.btn-primary,
.btn-secondary,
.btn-danger {
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 0.9em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #a080a8;
  color: white;
}

.btn-danger:hover {
  background: #8a6a8f;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(160, 128, 168, 0.4);
}

.btn-primary {
  background: #a080a8;
  color: white;
  flex: 1;
}

.btn-primary:hover {
  background: #8a6a8f;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(160, 128, 168, 0.4);
}

.btn-secondary {
  background: #f0f0f0;
  color: #666;
  flex: 1;
}

.btn-secondary:hover {
  background: #e0e0e0;
}

.qr-and-info-section {
  padding: 5px;
  background-color: #f8f9fa;
  border-radius: 8px;
}

.collapsible-header {
  cursor: pointer;
  user-select: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.collapsible-header:hover {
  opacity: 0.8;
}

.toggle-icon {
  color: #a080a8;
  font-size: 0.8em;
  margin-left: 8px;
  transition: transform 0.3s;
}

.qr-and-info-row {
  display: flex;
  gap: 30px;
}

.qr-section {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
}

.qr-section h3 {
  margin: 0 0 10px 0;
  font-size: 0.95em;
  color: #a080a8;
}

.qr-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

#qrcode {
  display: flex;
  justify-content: center;
  align-items: center;
}

#qrcode canvas {
  max-width: 150px;
  height: auto;
}

.campaign-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0px;
}

.campaign-info h3 {
  margin: 0 0 10px 0;
  font-size: 0.95em;
  color: #a080a8;
}

.qr-url-section {
  background: #f9f9f9;
  padding: 10px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 15px;
  width: 100%;
}

.btn-download {
  flex-shrink: 0;
  white-space: nowrap;
}

.qr-url {
  flex: 1;
  text-align: left;
  font-size: 0.85em;
  color: #666;
  display: flex;
  align-items: baseline;
}

.qr-url strong {
  flex-shrink: 0;
}

.qr-url a {
  color: #a080a8;
  text-decoration: none;
  word-break: break-all;
  padding-left: 0;
}

.qr-url a:hover {
  text-decoration: underline;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #e0e0e0;
  font-size: 0.9em;
}

.info-row.description-row {
  flex-direction: column;
  gap: 4px;
}

.info-row.description-row .value {
  font-style: italic;
  color: #666;
}

.info-row.description-row .value.editable:empty::before {
  content: '(No description - click to add)';
  color: #999;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row .label {
  font-weight: 600;
  color: #666;
  flex-shrink: 0;
}

.info-row .value {
  color: #333;
  flex: 1;
  min-height: 1.3em;
  padding: 2px 12px;
}

.info-row .value.editable {
  cursor: pointer;
  padding: 2px 6px;
  margin: -2px -6px;
  border-radius: 3px;
  transition: background-color 0.15s;
}

.info-row .value.editable:hover {
  background-color: #f0f0f0;
}

.edit-input {
  flex: 1;
  padding: 4px 8px;
  font-size: 0.95em;
  border: 2px solid #a080a8;
  border-radius: 4px;
  outline: none;
}

.question-builder {
  background-color: #f8f9fa;
  padding: 5px;
  border-radius: 8px;
}

.question-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 5px;
  border-bottom: 2px solid #a080a8;
  margin-bottom: 15px;
}

.question-title-and-buttons {
  display: flex;
  align-items: center;
  gap: 15px;
}

.question-title-and-buttons h3 {
  margin: 0;
  font-size: 0.95em;
  color: #a080a8;
  border-bottom: none;
  padding-bottom: 0;
}

.question-action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.toggle-icon-right {
  color: #a080a8;
  font-size: 0.9em;
  cursor: pointer;
  transition: transform 0.3s;
}

.toggle-icon-right:hover {
  opacity: 0.8;
}

.btn-icon-action {
  background: #a080a8;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9em;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon-action:hover {
  background: #8a6a8f;
  transform: translateY(-1px);
}

.btn-icon-action:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
}

.btn-icon-action:disabled:hover {
  background: #ccc;
  transform: none;
}

.questions-list {
  margin-bottom: 15px;
  max-height: 400px;
  overflow-y: auto;
  /* Invisible scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.questions-list::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.question-item {
  background-color: white;
  padding: 5px;
  margin-bottom: 5px;
  border-radius: 8px;
  border: 1px solid #ddd;
}

.question-number {
  font-weight: 600;
  font-size: 0.9em;
  color: #666;
}

.btn-icon {
  background: transparent;
  border: 1px solid #ccc;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-icon:hover:not(:disabled) {
  background-color: #e9ecef;
}

.btn-icon:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.questions-carousel {
  max-height: 350px;
  overflow-y: auto;
  /* Invisible scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.questions-carousel::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.question-content .form-row {
  display: flex;
  gap: 15px;
  align-items: flex-start;
}

.question-content .form-group {
  flex: 1;
  margin-bottom: 0;
}

.question-content .form-group.sequence-group {
  flex: 0 0 80px;
}

.question-type-select {
  padding: 6px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.9em;
  width: 100%;
}

.sequence-input {
  padding: 6px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.9em;
  width: 100%;
  text-align: center;
}

.question-text-input,
.options-input {
  width: 100%;
  padding: 6px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.9em;
  box-sizing: border-box;
  resize: vertical;
}

.question-navigation {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #e0e0e0;
}

.question-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.btn-nav {
  background: #a080a8;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9em;
  font-weight: 600;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-nav:hover:not(:disabled) {
  background: #8a6a8f;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(160, 128, 168, 0.4);
}

.btn-nav:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
}

.question-dots {
  display: flex;
  gap: 8px;
  align-items: center;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ddd;
  cursor: pointer;
  transition: all 0.3s;
}

.dot:hover {
  background: #bbb;
  transform: scale(1.2);
}

.dot.active {
  background: #a080a8;
  transform: scale(1.3);
}

.responses-section {
  background-color: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

.responses-section h3 {
  margin-top: 0;
  font-size: 0.95em;
  color: #a080a8;
}

.table-container {
  overflow-x: auto;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  max-height: 300px;
  overflow-y: auto;
  /* Invisible scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

.table-container::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

.responses-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8em;
}

.responses-table thead {
  background: #a080a8;
  color: white;
  position: sticky;
  top: 0;
}

.responses-table th {
  padding: 8px 6px;
  text-align: left;
  font-weight: 600;
  white-space: nowrap;
  font-size: 0.85em;
}

.responses-table td {
  padding: 6px;
  border-bottom: 1px solid #e0e0e0;
}

.clickable-row {
  cursor: pointer;
  transition: background-color 0.2s;
}

.responses-table tbody tr:hover {
  background: #f5f5f5;
}

.no-data {
  padding: 20px;
  text-align: center;
  color: #999;
}

.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
}

.popup-container {
  background-color: white;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.popup-header {
  background: #a080a8;
  color: white;
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 8px 8px 0 0;
}

.popup-header h3 {
  margin: 0;
}

.popup-actions {
  display: flex;
  gap: 10px;
}

.popup-actions .btn-icon {
  background: transparent;
  border: none;
  color: white;
  font-size: 1.2em;
  cursor: pointer;
  padding: 5px 10px;
}

.popup-actions .btn-icon:hover {
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}

.popup-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.respondent-info {
  margin-bottom: 20px;
}

.respondent-info .info-row {
  margin-bottom: 10px;
  color: #333;
}

.respondent-info .info-row-compact {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  color: #333;
  font-size: 0.9em;
}

.respondent-info .info-row-compact .field {
  white-space: nowrap;
  flex-shrink: 0;
}

.respondent-info .info-row-compact .field.email-field {
  width: 200px;
  min-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.respondent-info .info-row-compact .field strong {
  margin-right: 4px;
}

.print-only-title {
  display: none;
}

hr {
  border: none;
  border-top: 1px solid #ddd;
  margin: 20px 0;
}

hr.question-separator {
  border-top: 1px solid #e8e8e8;
  margin: 15px 0 0 0;
}

.answers-list {
  margin-top: 20px;
}

.answer-item {
  margin-bottom: 25px;
}

.question-text {
  margin-bottom: 8px;
  color: #333;
  font-size: 1.05em;
}

.answer-text {
  margin-left: 20px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
  white-space: pre-wrap;
  color: #555;
}

/* Print styles */
@media print {
  .print-only-title {
    display: block;
    margin-bottom: 20px;
    color: #333;
    text-align: center;
  }

  .respondent-info .info-row-compact {
    display: table !important;
    width: 100% !important;
    font-size: 0.85em;
  }

  .respondent-info .info-row-compact .field {
    display: table-cell !important;
    margin-right: 0 !important;
    padding-right: 20px !important;
    white-space: nowrap;
    vertical-align: top;
  }

  .respondent-info .info-row-compact .field.email-field {
    width: 200px;
    min-width: 200px;
  }

  .respondent-info .info-row-compact .field strong {
    margin-right: 8px !important;
  }
}

/* Smooth collapse/expand transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
